/**
 * Shared JS is intentional: Pages handlers are also imported by plain Node
 * in the production preview bridge, without a TypeScript loader.
 * Keep stacks and exception types, not request payloads or logger arguments.
 */
export function redactSentryText(value) {
  return value
    .replace(/\bBody:\s*[\s\S]*/gi, "Body: [redacted payload]")
    .replace(/\{[\s\S]*\}|\[[\s\S]*\]/g, "[redacted payload]")
    .replace(/Bearer\s+\S+/gi, "Bearer [redacted]")
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, "[redacted token]")
    .replace(/-----BEGIN [^-]*PRIVATE KEY-----[\s\S]*?-----END [^-]*PRIVATE KEY-----/g, "[redacted key]")
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, "[redacted email]")
    .replace(/\b(password|passcode|token|secret|api[_-]?key|authorization|cookie)\s*[:=]\s*["']?[^\s,;"']+/gi, "$1=[redacted]")
    .replace(/https?:\/\/[^\s"'<>]+/gi, (url) => {
      try {
        return new URL(url).origin + "/[redacted path]";
      } catch {
        return "[redacted URL]";
      }
    });
}

export function sanitizeSentryEvent(input) {
  try {
    return sanitizeEvent(input);
  } catch {
    // Do not recursively report a scrubber failure or send unfiltered data.
    return null;
  }
}

function sanitizeEvent(event) {
  // Never send request bodies/headers, scope user data, arbitrary extras,
  // contexts, attachment metadata or console/network breadcrumbs.
  delete event.request;
  delete event.user;
  delete event.extra;
  delete event.contexts;
  delete event.breadcrumbs;
  delete event.sdkProcessingMetadata;
  delete event.transaction;
  delete event.transaction_info;
  delete event.fingerprint;
  delete event.threads;
  // Pattern matching cannot guarantee arbitrary exception text is safe.
  if (typeof event.message === "string") event.message = "Exception details redacted";
  if (event.logentry) {
    event.logentry.message = "Exception details redacted";
    delete event.logentry.params;
    delete event.logentry.formatted;
  }
  for (const exception of event.exception?.values || []) {
    exception.value = "Exception details redacted";
    if (typeof exception.type !== "string" || !/^[A-Za-z][A-Za-z0-9_.]{0,79}$/.test(exception.type)) {
      exception.type = "Error";
    }
    delete exception.mechanism?.data;
    delete exception.mechanism?.meta;
    for (const frame of exception.stacktrace?.frames || []) {
      delete frame.vars;
      delete frame.pre_context;
      delete frame.context_line;
      delete frame.post_context;
      // Keep source-map paths intact, removing only credentials/query/hash.
      for (const key of ["filename", "abs_path"]) {
        if (typeof frame[key] === "string" && /^https?:\/\//i.test(frame[key])) {
          const url = new URL(frame[key]);
          url.username = "";
          url.password = "";
          url.search = "";
          url.hash = "";
          frame[key] = url.toString();
        }
      }
    }
  }
  if (event.tags) {
    const allowed = new Set(["operation", "handled_operation", "api_route", "api_method", "upstream_status"]);
    for (const key of Object.keys(event.tags)) {
      if (!allowed.has(key)) delete event.tags[key];
      else if (typeof event.tags[key] === "string") event.tags[key] = redactSentryText(event.tags[key]);
    }
  }
  return event;
}